import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskLines, SectionMark } from "./ui/Primitives";
import { disciplines, projects } from "../data/projects";

const differentiators = [
  {
    title: "לא תבניות. לא שבלונות.",
    body: "כל פרויקט נבנה מאפס ומותאם לעסק שלכם, לא עוד אתר שנראה כמו כולם.",
  },
  {
    title: "אנחנו כותבים קוד",
    body: "React, Next.js ו-AI. פיתוח אמיתי בידי מהנדסים, לא גרירה ושחרור בבונה אתרים.",
  },
  {
    title: "טכנולוגיה שחושבת קדימה",
    body: "סוכנים חכמים, אוטומציה ו-Big Data. כלים שרוב הסוכנויות לא יודעות שקיימים.",
  },
  {
    title: "מייסד שעבר הכל",
    body: "כשמייסד החברה ניצח סרטן, הוא למד שאין דבר שאי אפשר להפוך להצלחה.",
  },
];

const About: React.FC = () => (
  <>
    {/* Opening statement */}
    <section className="bg-ink pb-16 pt-28 lg:pb-24 lg:pt-36">
      <div className="shell">
        <MaskLines
          as="h1"
          className="t-display max-w-[13ch] text-fg"
          lines={["לא סתם סוכנות.", "שותפים לצמיחה."]}
        />

        <div className="mt-16 grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-20">
          <Reveal delay={120}>
            <div className="space-y-6">
              <p className="t-lead">
                ב-ELAD DIGITAL אנחנו מאמינים שכל עסק ראוי לנוכחות דיגיטלית ברמה
                הגבוהה ביותר. עם רקע עשיר באמנות, חדשנות ויזמות, ניסיון של מעל
                25 שנה וניהול קמפיינים בהיקפים של מיליונים, אנחנו הופכים חזון
                דיגיטלי למציאות שמניבה הצלחה.
              </p>
              <p className="t-body">
                אנחנו לא רק בונים אתרים או מריצים קמפיינים. אנחנו מפתחים
                אפליקציות מובייל שיוצאות לחנויות, בונים סוכנים חכמים ומערכות
                אוטומציה, מנתחים Big Data ויוצרים דשבורדים שהופכים נתונים
                להחלטות.
              </p>
            </div>
          </Reveal>

          <Reveal delay={220}>
            <dl className="grid grid-cols-2 gap-px border border-[var(--line)] bg-[var(--line)] lg:grid-cols-1">
              {[
                { v: "25+", k: "שנות ניסיון" },
                { v: String(projects.length), k: "עבודות נבחרות בתיק" },
                { v: String(disciplines.length), k: "תחומי פיתוח" },
                { v: "24/7", k: "זמינות" },
              ].map((item) => (
                <div key={item.k} className="bg-ink p-5">
                  <dt className="t-data text-3xl text-signal" dir="ltr">
                    {item.v}
                  </dt>
                  <dd className="mt-1 text-sm text-fg-2">{item.k}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Why us */}
    <section className="border-t border-[var(--line)] bg-ink-2 py-24 lg:py-32">
      <div className="shell">
        <Reveal>
          <SectionMark label="למה אנחנו" />
        </Reveal>

        <MaskLines
          as="h2"
          className="t-h1 mb-14 max-w-[18ch] text-fg"
          lines={["ארבע סיבות", "לעבוד איתנו"]}
        />

        <ul className="border-t border-[var(--line)]">
          {differentiators.map((item, i) => (
            <Reveal as="li" key={item.title} delay={i * 70}>
              <div className="grid gap-2 border-b border-[var(--line)] py-7 sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1.4fr)] sm:items-baseline sm:gap-8">
                <span className="t-data text-xs text-fg-3" dir="ltr">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="t-h3 text-fg">{item.title}</h3>
                <p className="text-fg-2">{item.body}</p>
              </div>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>

    {/* The person behind it */}
    <section className="bg-ink py-24 lg:py-32">
      <div className="shell">
        <Reveal>
          <SectionMark label="הסיפור מאחורי המותג" />
        </Reveal>

        <MaskLines
          as="h2"
          className="t-h1 mb-14 max-w-[16ch] text-fg"
          lines={["אלעד קרן", "היזם שניצח את הכל"]}
        />

        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <Reveal>
            <img
              src="/images/elad-6.jpeg"
              alt="אלעד קרן"
              loading="lazy"
              className="w-full object-cover"
              style={{ aspectRatio: "4 / 5" }}
            />
          </Reveal>

          <Reveal delay={140}>
            <div className="space-y-6">
              <p className="t-body">
                אלעד קרן הוא יזם, מוזיקאי, בסיסט והייטקיסט עם ניסיון של מעל 25
                שנה בעולם הדיגיטלי. אב לבת, אומן ויוצר. אדם שכבר ראה את המוות
                בעיניים, כולם הספידו אותו, והוא חזר כדי לספר את הסיפור.
              </p>
              <p className="t-body">
                בגיל 38 אובחן אלעד עם סרטן דם. אחרי ארבעה סבבי טיפול כימי מצבו
                הוגדר כסופני ומשקלו ירד ל-45 ק״ג. כנגד כל הסיכויים, בזכות תרופה
                ניסיונית ורצון עז לחיות, הוא החלים.
              </p>
              <p className="t-body">
                כיום אלעד הוא מנטור שמלווה אנשים שנתבשרו שחלו בלוקמיה. את סיפורו
                המלא הוא מספר בספרו{" "}
                <span className="font-medium text-fg">
                  ״סקס, סמים ומחלות קשות״
                </span>
                .
              </p>

              <blockquote className="border-t border-[var(--line)] pt-6">
                <p className="font-display text-xl leading-snug text-fg">
                  ״דברים לא קורים סתם. לכל דבר יש סיבה ומשמעות, גם אם לא מבינים
                  אותה באותו רגע.״
                </p>
                <cite className="t-meta mt-4 block not-italic">אלעד קרן</cite>
              </blockquote>

              <div className="flex flex-wrap gap-3 pt-2">
                <a
                  href="https://www.eladkeren.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                >
                  האתר האישי
                </a>
                <Link to="/contact" className="btn btn-primary">
                  <span>נדבר על הצמיחה שלכם</span>
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);

export default About;
